<script lang="ts">
  import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
  import _axios from 'axios'
  import { getAuthorizationToken } from '../../../auth'
  import {
    SUPPORT_SESSION_STORAGE_KEYS,
    useSupportSession
  } from '../../../support-session'
  import {
    isProblemDetails,
    type ProblemDetails
  } from '../../../http-client/http-client-types'
  import { API_REQUEST_HEADERS } from '../../../http-client/api-request-headers'
  import { FEATURE_FLAGS } from '../../../feature-flag'
  import { ApiResponseError } from '../../../error-handling/ApiResponseError'
  import {
    appSessionId,
    catchApiError,
    getBaseApiUrl
  } from '../../utils/gs-cloud-backend-utils'
  import { inject, provide, watch } from 'vue'

  const showDebug = FEATURE_FLAGS.IS_DEV || FEATURE_FLAGS.IS_STAGING

  function defineAxiosInstance() {
    const axiosInstance = _axios.create({
      baseURL: getBaseApiUrl(),
      headers: {
        common: {
          'Content-Type': 'application/json',
          [API_REQUEST_HEADERS.GATSHIP_APP_SESSION_ID]: appSessionId
        }
      }
    })

    /** Assigns a set of headers to the axios instance. */
    function setAxiosHeaders(headers: { [key: string]: string }) {
      Object.entries(headers).forEach(([key, value]) => {
        setAxiosHeader(key, value)
      })
    }

    /** Assign a single header to the axios instance. */
    function setAxiosHeader(propertyName: string, value: string) {
      axiosInstance.defaults.headers.common[propertyName] = value
    }

    axiosInstance.interceptors.request.use(
      async (config) => {
        const _authorizationToken = await getAuthorizationToken()
        const { isSupportSession, fetchSupportSessionTokenIfExpired } =
          useSupportSession({
            inject: false
          })
        if (isSupportSession.value) {
          const _supportSessionToken =
            await fetchSupportSessionTokenIfExpired(_authorizationToken)
          config.headers.set(
            SUPPORT_SESSION_STORAGE_KEYS.SESSION_TOKEN,
            _supportSessionToken
          )
        }

        config.headers.set(
          API_REQUEST_HEADERS.AUTHORIZATION,
          `Bearer ${_authorizationToken}`
        )
        return config
      },
      (error) => {
        Promise.reject(error)
      }
    )

    function createConfigForRequest(
      commandId: string,
      config?: AxiosRequestConfig
    ): AxiosRequestConfig {
      const result = config ?? {}
      let headers = {
        ...axiosInstance.defaults.headers.common,
        [API_REQUEST_HEADERS.GATSHIP_COMMAND_ID]: commandId
      }
      if (config && config.headers) {
        headers = { ...headers, ...config.headers }
      }
      result.headers = headers
      return result
    }

    async function apiPut<T, U = unknown>(
      path: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: U,
      commandId: string,
      config: AxiosRequestConfig | undefined = {
        headers: axiosInstance.defaults.headers.common
      }
    ): Promise<T> {
      if (showDebug) {
        console.log(`apiPut: ${path} \r\n`, payload)
      }
      const configWithHeaders = createConfigForRequest(commandId, config)
      const [error, response] = await catchApiError<
        AxiosResponse<T>,
        ProblemDetails | AxiosError
      >(axiosInstance.put<T>(path, payload, configWithHeaders))
      if (showDebug) {
        console.log(`Result ${path}`, response?.data)
      }
      if (error) {
        throw new ApiResponseError({
          message: isProblemDetails(error)
            ? (error.friendlyTitle ?? error.title)
            : error.message,
          apiError: error,
          payload
        })
      }
      return response.data
    }

    async function apiDelete<T, U = unknown>(
      path: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: U,
      commandId: string,
      config?: AxiosRequestConfig
    ): Promise<T> {
      if (showDebug) {
        console.log(`apiDelete: ${path}`)
      }

      const configWithHeaders = createConfigForRequest(commandId, config)

      const [error, response] = await catchApiError<
        AxiosResponse<T>,
        ProblemDetails | AxiosError
      >(axiosInstance.delete<T>(path, { ...configWithHeaders, data: payload }))
      if (showDebug) {
        console.log(`Result ${path}`, response?.data)
      }
      if (error) {
        throw new ApiResponseError({
          message: isProblemDetails(error)
            ? (error.friendlyTitle ?? error.title)
            : error.message,
          apiError: error,
          payload
        })
      }
      return response.data
    }

    async function apiPost<T, U = unknown>(
      path: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: U,
      commandId: string,
      config: AxiosRequestConfig | undefined = {
        headers: axiosInstance.defaults.headers.common
      }
    ): Promise<T> {
      if (showDebug) {
        console.log(`apiPost: ${path}`)
      }
      const configWithHeaders = createConfigForRequest(commandId, config)

      const [error, response] = await catchApiError<
        AxiosResponse<T>,
        ProblemDetails | AxiosError
      >(axiosInstance.post<T>(path, payload, configWithHeaders))
      if (showDebug) {
        console.log(`Result ${path}`, response?.data)
      }
      if (error) {
        throw new ApiResponseError({
          message: isProblemDetails(error)
            ? (error.friendlyTitle ?? error.title)
            : error.message,
          apiError: error,
          payload
        })
      }
      return response.data
    }

    async function apiGet<T>(
      path: string,
      config: AxiosRequestConfig | undefined = {
        headers: axiosInstance.defaults.headers.common
      }
    ): Promise<T> {
      if (showDebug) {
        console.log(`apiGet: ${path}`)
      }
      const [error, response] = await catchApiError<
        AxiosResponse<T>,
        ProblemDetails | AxiosError
      >(axiosInstance.get<T>(path, config))
      if (showDebug) {
        console.log(`Result ${path} `, response?.data)
      }
      if (error) {
        throw new ApiResponseError({
          message: isProblemDetails(error)
            ? (error.friendlyTitle ?? error.title)
            : error.message,
          apiError: error,
          payload: undefined
        })
      }
      return response.data
    }

    return {
      apiGet,
      apiPost,
      apiPut,
      apiDelete,
      setAxiosHeaders,
      setAxiosHeader
    }
  }
  export type GsCloudBackendProvider = ReturnType<typeof defineAxiosInstance>

  export const gsCloudBackendProviderKey = Symbol(
    'gs-cloud-backend-provider'
  ) as InjectionKey<GsCloudBackendProvider>

  export function useGsCloudBackend() {
    const gsCloudBackendProvider = inject(gsCloudBackendProviderKey)

    if (!gsCloudBackendProvider) {
      throw new Error(
        'could not find gs-cloud-backend-provider. Did you provide it in your app?'
      )
    }
    return gsCloudBackendProvider
  }

  const defaultGsCloudBackendProvider = defineAxiosInstance()

  const { apiGet, apiPost, apiPut, apiDelete } = defaultGsCloudBackendProvider

  export { apiGet, apiPost, apiPut, apiDelete }
</script>
<script setup lang="ts">
  const props = defineProps<{
    /** Do not use this prop. It is for internal use only. */
    _isGlobal?: boolean
    name?: string
    databaseId?: string
  }>()

  const gsCloudBackendProvider = props._isGlobal
    ? defaultGsCloudBackendProvider
    : defineAxiosInstance()

  watch(
    () => props.databaseId,
    (newVal) => {
      if (newVal) {
        gsCloudBackendProvider.setAxiosHeader(
          API_REQUEST_HEADERS.GATSHIP_ACTIVE_TENANT_DB_INSTANCE,
          newVal
        )
      }
    }
  )
  provide(gsCloudBackendProviderKey, gsCloudBackendProvider)
  defineExpose(gsCloudBackendProvider)
</script>
<template>
  <slot />
</template>
